import { useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { createPortal } from "react-dom";
import { RESOURCES, setLabel } from "../gameMeta";
import type { CardDef } from "../types";
import { ResourceBadge } from "./ResourceIcon";

const VIEWPORT_MARGIN = 8;
const CARD_GAP = 10;

interface RectLike {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
}

interface Size {
  width: number;
  height: number;
}

export interface CardExplanationPosition {
  left: number;
  top: number;
  placement: "boven" | "onder";
}

export function calculateCardExplanationPosition(
  anchor: RectLike,
  tooltip: Size,
  viewport: Size,
): CardExplanationPosition {
  const maximumLeft = Math.max(VIEWPORT_MARGIN, viewport.width - tooltip.width - VIEWPORT_MARGIN);
  const preferredLeft = anchor.left + anchor.width / 2 - tooltip.width / 2;
  const left = clamp(preferredLeft, VIEWPORT_MARGIN, maximumLeft);
  const spaceAbove = anchor.top - CARD_GAP;
  const spaceBelow = viewport.height - anchor.bottom - CARD_GAP;
  const placement = spaceAbove >= tooltip.height || (spaceBelow < tooltip.height && spaceAbove >= spaceBelow)
    ? "boven"
    : "onder";
  const preferredTop = placement === "boven"
    ? anchor.top - CARD_GAP - tooltip.height
    : anchor.bottom + CARD_GAP;
  const maximumTop = Math.max(VIEWPORT_MARGIN, viewport.height - tooltip.height - VIEWPORT_MARGIN);

  return {
    left,
    top: clamp(preferredTop, VIEWPORT_MARGIN, maximumTop),
    placement,
  };
}

export function cardTypeLabel(definition: CardDef): string {
  if (definition.unit_kind === "held") return "Held";
  if (definition.unit_kind === "handelsschip") return "Handelsschip";
  return {
    actie: "Actiekaart",
    nederzettingsgebouw: "Nederzettingsgebouw",
    nederzettingseenheid: "Nederzettingseenheid",
    stadsgebouw: "Stadsgebouw",
    landschapsuitbouw: "Landschapsuitbouw",
    gebeurtenis: "Gebeurteniskaart",
  }[definition.kind];
}

export function CardExplanation({
  anchorRef,
  definition,
  id,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  definition: CardDef;
  id: string;
}) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<CardExplanationPosition | null>(null);

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchor = anchorRef.current;
      const tooltip = tooltipRef.current;
      if (!anchor || !tooltip) return;
      const next = calculateCardExplanationPosition(
        anchor.getBoundingClientRect(),
        { width: tooltip.offsetWidth, height: tooltip.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
      );
      setPosition((current) => current
        && current.left === next.left
        && current.top === next.top
        && current.placement === next.placement
        ? current
        : next);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef, definition.id]);

  const costs = definition.cost.flatMap((amount, index) => amount ? [{ amount, resource: RESOURCES[index] }] : []);
  const stats = [
    ["Zegepunten", definition.stats.victory],
    ["Krachtpunten", definition.stats.strength],
    ["Handelspunten", definition.stats.commerce],
    ["Vaardigheidspunten", definition.stats.skill],
    ["Vooruitgangspunten", definition.stats.progress],
  ].filter((entry): entry is [string, number] => Number(entry[1]) > 0);

  return createPortal(
    <div
      ref={tooltipRef}
      id={id}
      role="tooltip"
      className={`card-explanation set-${definition.set}`}
      data-card-explanation={definition.id}
      data-placement={position?.placement}
      style={position ? { left: position.left, top: position.top } : { left: -10_000, top: -10_000, visibility: "hidden" }}
    >
      <header>
        <span>Kaartuitleg</span>
        <strong>{definition.name}</strong>
      </header>
      <div className="card-explanation-meta">
        <span>{cardTypeLabel(definition)}</span>
        <span>{setLabel(definition.set)}</span>
        {definition.unique ? <span>Uniek</span> : null}
      </div>
      <p>{definition.text}</p>
      <section className="card-explanation-cost" aria-label="Kaartkosten">
        <b>Kosten</b>
        <div>
          {costs.length ? costs.map(({ resource, amount }) => (
            <ResourceBadge key={resource} resource={resource} amount={amount} compact />
          )) : <span>Geen grondstofkosten</span>}
        </div>
      </section>
      {stats.length ? (
        <dl className="card-explanation-stats" aria-label="Kaartpunten">
          {stats.map(([label, value]) => (
            <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
          ))}
        </dl>
      ) : null}
    </div>,
    document.body,
  );
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}
